/**
 * Recommend products based on user's browsing history.
 * Supports complementary or related product recommendations.
 */

if (!customElements.get("recommendation-by-history")) {
  customElements.define(
    "recommendation-by-history",
    class RecommendationByHistory extends HTMLElement {
      constructor() {
        super();

        this.section = this.dataset.section;
      }

      disconnectedCallback() {
        this.abortFetch();
      }

      /**
       * If there is a recently viewed product, show related products; otherwise hide.
       */
      showContent() {
        this.newestProductId = this.getNewestProductId();
        if (this.newestProductId == null) return this.toggleShow(false);

        if (this.newestProductId === this.preProductId) return;

        this.preProductId = this.newestProductId;
        this.url = this.constructUrl();

        this.toggleShow();

        if (this.url) {
          this.fetchContent(this.url);
        } else {
          this.toggleShow(false);
        }
      }

      /**
       * Get the most recently viewed product ID.
       * @returns {string|null}
       */
      getNewestProductId() {
        const recentlyViewedProductIds =
          webvista.retrieveData(RECENTLY_VIEWED_KEY) || [];
        if (recentlyViewedProductIds.length === 0) return null;

        return recentlyViewedProductIds[recentlyViewedProductIds.length - 1];
      }

      /**
       * Construct the fetch URL with product ID.
       * @returns {string|null}
       */
      constructUrl() {
        const url = this.dataset.url;
        if (!url) return null;

        return `${url}&product_id=${this.newestProductId}`;
      }

      /**
       * Fetch recommended products from the server.
       */
      fetchContent(url) {
        if (this.fetchController) this.fetchController.abort();
        this.fetchController = new AbortController();
        const signal = this.fetchController.signal;

        fetch(url, { signal })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Network response was not ok: ${response.statusText}`,
              );
            }
            return response.text();
          })
          .then((html) => {
            const sourceDom = new DOMParser().parseFromString(
              html,
              "text/html",
            );
            const sourceRecommendationMain = sourceDom.querySelector(
              `#${this.id} .recommendation-slider`,
            );

            if (sourceRecommendationMain) {
              this.replaceChildren(sourceRecommendationMain.cloneNode(true));
              webvista.initLazyImages();
              webvista.initToolTips();
            } else {
              this.toggleShow(false);
            }
          })
          .catch((error) => {
            if (error.name !== "AbortError") this.toggleShow(false);
          });
      }

      /**
       * Toggle visibility state.
       * @param {boolean} show - Whether to show the component
       */
      toggleShow(show = true) {
        if (show) {
          // Show loading placeholder
          this.placeholderTemplate =
            this.placeholderTemplate ||
            this.querySelector(`#Placeholder-Template-${this.section}`);

          if (this.placeholderTemplate) {
            this.replaceChildren(
              this.placeholderTemplate.content.cloneNode(true),
            );
          }
        }

        this.setAttribute("aria-hidden", !show);
        this.classList.toggle("hidden", !show);
      }

      /**
       * Abort any pending fetch request.
       */
      abortFetch() {
        if (this.fetchController) this.fetchController.abort();
      }
    },
  );
}
